/**
 * usePhoneOTP — shared hook for phone OTP verification
 *
 * Handles the full OTP lifecycle:
 *   - Sending OTP via WhatsApp (with optional onBeforeSend)
 *   - Verifying OTP code
 *   - 30-second resend timer
 *   - Reset on number change
 *
 * Usage:
 *   const otp = usePhoneOTP();
 *   otp.handleSendOTP(phoneNumber);
 *   otp.handleVerifyOTP(phoneNumber, otpCode);
 *   {otp.phoneVerified && <VerifiedBadge />}
 *   {otp.otpSent && !otp.phoneVerified && <OTPInput />}
 *
 * With onBeforeSend (for flows that need to create user first):
 *   const otp = usePhoneOTP({ onBeforeSend: async (phone) => { await createUser(phone); } });
 */

import { useState, useEffect, useCallback } from "react";
import api from "../api/axios";

export function usePhoneOTP({ onBeforeSend } = {}) {
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [otpError, setOtpError] = useState("");

  /* ── Timer countdown ── */
  useEffect(() => {
    if (otpTimer <= 0) return;
    const interval = setInterval(() => setOtpTimer((t) => t - 1), 1000);
    return () => clearInterval(interval);
  }, [otpTimer]);

  /* ── Send OTP (with optional onBeforeSend for user creation) ── */
  const handleSendOTP = useCallback(async (phone) => {
    if (!phone) {
      setOtpError("Enter phone number first");
      return false;
    }

    try {
      setOtpLoading(true);
      setOtpError("");

      // Optional pre-step (e.g. create user account before sending OTP)
      if (onBeforeSend) {
        await onBeforeSend(phone);
      }

      const res = await api.post("/users/send-otp-whatsapp", {
        phoneNumber: phone,
      });

      if (res.data.success) {
        setOtpSent(true);
        setOtpTimer(30);
        return true;
      }
      return false;
    } catch (err) {
      setOtpError(err.response?.data?.message || "Failed to send OTP");
      return false;
    } finally {
      setOtpLoading(false);
    }
  }, [onBeforeSend]);

  /* ── Verify OTP ── */
  const handleVerifyOTP = useCallback(async (phone, code) => {
    if (!code || code.length < 4) {
      setOtpError("Enter a valid OTP");
      return false;
    }

    try {
      setOtpLoading(true);
      setOtpError("");

      const res = await api.post("/users/verify-otp", {
        phoneNumber: phone,
        otpCode: code,
      });

      if (res.data.success) {
        setPhoneVerified(true);
        setOtpSent(false);
        setOtpCode("");
        return true;
      }
      return false;
    } catch (err) {
      setOtpError(err.response?.data?.message || "Invalid OTP");
      return false;
    } finally {
      setOtpLoading(false);
    }
  }, []);

  /* ── Reset when phone number changes ── */
  const resetOTP = useCallback(() => {
    setPhoneVerified(false);
    setOtpSent(false);
    setOtpCode("");
    setOtpError("");
    setOtpTimer(0);
  }, []);

  return {
    phoneVerified,
    setPhoneVerified,
    otpSent,
    otpCode,
    otpLoading,
    otpTimer,
    otpError,
    setOtpCode,
    handleSendOTP,
    handleVerifyOTP,
    resetOTP,
  };
}